import React from 'react';
import ApiKeyContainer from '../../../state/containers/ApiKeyContainer';
import OrganizationContainer from '../../../state/containers/OrganizationContainer';
import MediaContainer from '../../../state/containers/MediaContainer';
import _ from 'lodash';
import UserContainer from '../../../state/containers/UserContainer';
import DeploymentAccountsContainer from '../../../state/containers/DeploymentAccountsContainer';
import ApplicationContainer from '../../../state/containers/ApplicationContainer';
import AppliedApplicationTemplateContainer from '../../../state/containers/AppliedApplicationTemplateContainer';
import ApplicationEnvironmentsContainer from '../../../state/containers/ApplicationEnvironmentsContainer';
import TabContainerPanel from '../../../components/structure/Panels/TabContainerPanel';
import { TabPanel } from 'react-tabs';
import ScrollablePanelBody from '../../../components/structure/ScrollablePanelBody';
import OrganizationInfoSettings from './OrganizationInfoSettings';
import ApiKeySettings from './ApiKeySettings';
import GenerateApiKeyModal from './GenerateApiKeyModal';
import OrganizationDeploymentAccounts from './OrganizationDeploymentAccounts';
import OrganizationVariablesSettings from './OrganizationVariablesSettings';
import OrganizationManagementSettings from './OrganizationManagementSettings';
import OrganizationHealthcareManagementSettings from './OrganizationHealthcareManagementSettings';
import ManagedWorkQueuePanel from './ManagedWorkQueuePanel';
import ResponseVariableTestSettings from './ResponseVariableTestSettings';
import Button from '../../../components/general/Button';
import Loader from '../../../components/general/Loader';
import MemberContainer from '../../../state/containers/MemberContainer';
import ScheduledEmailForm from '../../../components/forms/ScheduledEmailForm';
import { css } from '@emotion/css';
import { HealthcareSchedulingFeatureFlagId } from '../../../constants/featureFlagIds';
import OrganizationStatusSettings from './OrganizationStatusSettings';

const apiKeyBlue = require('../../../content/images/organization-settings/api-key-blue.svg');
const apiKeyDark = require('../../../content/images/organization-settings/api-key-dark.svg');
const credentialsDark = require('../../../content/images/organization-settings/credentials-dark.svg');
const credentialsBlue = require('../../../content/images/organization-settings/credentials-blue.svg');
const basicInfoBlue = require('../../../content/images/organization-settings/basic-info-blue.svg');
const basicInfoDark = require('../../../content/images/organization-settings/basic-info-dark.svg');
const addIcon = require("../../../content/images/content-explorer/add-white.svg");
const constantsIcon = require("../../../content/images/tab-icons/constants-dark.svg");
const constantsBlueIcon = require("../../../content/images/tab-icons/constants-blue.svg");
const sendEmailIcon = require('../../../content/images/read-email-monitor.svg');
const responseVarIcon = require("../../../content/images/tab-icons/response-variables-dark.svg");
const responseVarBlueIcon = require("../../../content/images/tab-icons/response-variables-blue.svg");

interface OrgnaizationSettingsProps {
    orgContainer: OrganizationContainer,
    mediaContainer: MediaContainer,
    applicationContainer: ApplicationContainer,
    userContainer: UserContainer,
    deploymentContainer: DeploymentAccountsContainer,
    apiKeyContainer: ApiKeyContainer,
    memberContainer: MemberContainer,
    appliedTemplateContainer: AppliedApplicationTemplateContainer,
    applicationEnvironmentsContainer: ApplicationEnvironmentsContainer,
    history: any
}
interface OrgnaizationSettingsState {
    isNewApiKeyModal: boolean;
    hasHealthcareApps: boolean | null;
}
class OrganizationSettingsTabs extends React.Component<OrgnaizationSettingsProps, OrgnaizationSettingsState> {
    constructor(props) {
        super(props);

        this.state = {
            isNewApiKeyModal: false,
            hasHealthcareApps: null
        }
    }

    async componentDidMount() {
        if (!this.props.userContainer.state.currentUser) {
            await this.props.userContainer.getUser()
        }
        await this.props.memberContainer.checkIsOrgAdmin(this.props.orgContainer.state.currentOrganization?.id, this.props.userContainer.state.currentUser?.id);

        // Load apps and check for healthcare
        const organizationId = this.props.orgContainer.state.currentOrganization?.id;
        if (organizationId) {
            // Check if apps are already loaded for this org
            const existingApps = this.props.applicationContainer.state.baseOrgApplications;
            const existingOrgId = this.props.applicationContainer.state.organizationId;

            if (existingApps?.length > 0 && existingOrgId === organizationId) {
                // Apps already loaded for this org - use them directly
                const hasHealthcare = existingApps.some(app =>
                    app.featureFlags?.some(flag => flag.id === HealthcareSchedulingFeatureFlagId)
                ) || false;
                this.setState({ hasHealthcareApps: hasHealthcare });
            } else {
                // Need to load apps
                const result = await this.props.applicationContainer.getBaseApplicationsForOrg(organizationId);
                const apps = result?.data || this.props.applicationContainer.state.baseOrgApplications || [];
                const hasHealthcare = apps.some(app =>
                    app.featureFlags?.some(flag => flag.id === HealthcareSchedulingFeatureFlagId)
                ) || false;
                this.setState({ hasHealthcareApps: hasHealthcare });
            }
        } else {
            this.setState({ hasHealthcareApps: false });
        }
    }

    getSelectedIndex() {
        if (location.href.indexOf("apiKeys") > -1) {
            return 1;
        }
        if (location.href.indexOf("credentials") > -1) {
            return 2;
        }
        if (location.href.indexOf("constants") > -1) {
            return 3;
        }
        if (location.href.indexOf("management") > -1) {
            return 4;
        }
        if (location.href.indexOf("responseVariables") > -1) {
            return 5;
        }
        if (location.href.indexOf("emails") > -1) {
            return 6;
        }
        return 0;
    }

    handleTabSelection(index: number, last: number) {
        if (index == last) return;
        const urlStart = "/v/orgSettings/";

        if (index == 0) {
            this.props.history.push(urlStart + window.location.search)
        }
        if (index == 1) {
            this.props.history.push(urlStart + "apiKeys" + window.location.search)
        }
        if (index == 2) {
            this.props.history.push(urlStart + "credentials" + window.location.search)
        }
        if (index == 3) {
            this.props.history.push(urlStart + "constants" + window.location.search)
        }
        if (index == 4) {
            this.props.history.push(urlStart + "management" + window.location.search)
        }
        if (index == 5) {
            this.props.history.push(urlStart + "responseVariables" + window.location.search)
        }
        if (index == 6) {
            this.props.history.push(urlStart + "emails" + window.location.search)
        }
    }

    renderAddNewApiKeyButton() {
        if (this.getSelectedIndex() == 1 && this.props.memberContainer.state.isOrgAdmin) {
            return (
                <Button
                    themes={['primary-small']}
                    text="Add New"
                    onClick={() => this.toggleNewApiKeyModal()}
                    type="button"
                    rightIcon={addIcon} />
            );
        }
        return null;
    }

    toggleNewApiKeyModal() {
        this.setState({
            ...this.state,
            isNewApiKeyModal: !this.state.isNewApiKeyModal
        });
    }

    render() {
        return (
            <TabContainerPanel
                selectedIndex={this.getSelectedIndex()}
                onTabSelected={this.handleTabSelection.bind(this)}
                tabs={[{
                    title: 'Basic Info',
                    icon: basicInfoDark,
                    className: "ac-org-settings-basic-info",
                    selectedIcon: basicInfoBlue
                }, {
                    title: 'Api Keys',
                    icon: apiKeyDark,
                    className: "ac-org-settings-api-keys",
                    selectedIcon: apiKeyBlue
                }, {
                    title: 'Credentials',
                    icon: credentialsDark,
                    className: "ac-org-settings-credentials",
                    selectedIcon: credentialsBlue
                }, {
                    title: 'Constants',
                    icon: constantsIcon,
                    className: "ac-org-settings-constants",
                    selectedIcon: constantsBlueIcon
                },
                {
                    title: 'Management',
                    icon: constantsIcon,
                    className: "ac-org-settings-management",
                    selectedIcon: constantsBlueIcon
                },
                {
                    title: 'Debugger',
                    icon: responseVarIcon,
                    className: "ac-org-settings-response-variables",
                    selectedIcon: responseVarBlueIcon
                },
                {
                    title: 'Emails',
                    icon: sendEmailIcon,
                    selectedIcon: sendEmailIcon
                }]}
                additionalHeaderContent={this.renderAddNewApiKeyButton.bind(this)}>
                <TabPanel>
                    <ScrollablePanelBody className={paddedBody}>
                        <OrganizationInfoSettings orgContainer={this.props.orgContainer} mediaContainer={this.props.mediaContainer} />
                        {this.props.userContainer.state.currentUser?.canCreateOrganizations === true &&
                            <OrganizationStatusSettings orgContainer={this.props.orgContainer} />
                        }
                    </ScrollablePanelBody>
                </TabPanel>
                <TabPanel>
                    <ApiKeySettings isOrgAdmin={this.props.memberContainer.state.isOrgAdmin} orgContainer={this.props.orgContainer} apiKeyContainer={this.props.apiKeyContainer} />
                    {this.state.isNewApiKeyModal &&
                        <GenerateApiKeyModal apiKeyContainer={this.props.apiKeyContainer} onClose={this.toggleNewApiKeyModal.bind(this)} organizationId={this.props.orgContainer.state.currentOrganization?.id} />
                    }
                </TabPanel>
                <TabPanel>
                    <ScrollablePanelBody className={paddedBody}>
                        <OrganizationDeploymentAccounts organizationId={this.props.orgContainer.state.currentOrganization?.id} deploymentAccountsContainer={this.props.deploymentContainer} />
                    </ScrollablePanelBody>
                </TabPanel>
                <TabPanel>
                    <ScrollablePanelBody className={paddedBody}>
                        <OrganizationVariablesSettings organizationId={this.props.orgContainer.state.currentOrganization?.id} />
                    </ScrollablePanelBody>
                </TabPanel >
                <TabPanel>
                    <ScrollablePanelBody className={paddedBody}>
                        {this.props.userContainer.state.currentUser?.canCreateOrganizations &&
                            <ManagedWorkQueuePanel
                                organizationId={this.props.orgContainer.state.currentOrganization?.id}
                                organizationName={this.props.orgContainer.state.currentOrganization?.name}
                                organizations={this.props.orgContainer.state.organizations}
                            />
                        }
                        {this.state.hasHealthcareApps === null ? (
                            <Loader />
                        ) : this.state.hasHealthcareApps ? (
                            <OrganizationHealthcareManagementSettings
                                organizationId={this.props.orgContainer.state.currentOrganization?.id}
                                canManageQueues={this.props.userContainer.state.currentUser?.canCreateOrganizations === true}
                                applicationContainer={this.props.applicationContainer}
                                appliedTemplateContainer={this.props.appliedTemplateContainer}
                                applicationEnvironmentsContainer={this.props.applicationEnvironmentsContainer}
                            />
                        ) : (
                            <OrganizationManagementSettings
                                organizationId={this.props.orgContainer.state.currentOrganization?.id}
                                canManageQueues={this.props.userContainer.state.currentUser?.canCreateOrganizations === true}
                                applicationContainer={this.props.applicationContainer}
                                appliedTemplateContainer={this.props.appliedTemplateContainer}
                                applicationEnvironmentsContainer={this.props.applicationEnvironmentsContainer}
                            />
                        )}
                    </ScrollablePanelBody>
                </TabPanel>
                <TabPanel>
                    <ScrollablePanelBody className={paddedBody}>
                        <ResponseVariableTestSettings
                            organizationId={this.props.orgContainer.state.currentOrganization?.id}
                        />
                    </ScrollablePanelBody>
                </TabPanel>
                <TabPanel>
                    <ScrollablePanelBody className={paddedBody} autoHeight={true} autoHeightMax={"calc(100vh - 136px)"}>
                        <ScheduledEmailForm organizationId={this.props.orgContainer.state.currentOrganization?.id} orgName={this.props.orgContainer.state.currentOrganization?.name} appContainer={this.props.applicationContainer} />
                    </ScrollablePanelBody>
                </TabPanel>
            </TabContainerPanel >
        )
    }
}

const paddedBody = css`
padding: 32px;
`
export default OrganizationSettingsTabs;
